package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.presentation.LocationSearchDialog

/**
 * Filter row above the Loads board: a Customer dropdown and (on the Dispatch tab) a Driver
 * dropdown, plus a Clear Filters action. The selection is applied client-side via
 * [LoadsState.filteredLoads], so each change just routes a [LoadsEvent].
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun LoadsFilterBar(
    state: LoadsState,
    showEmployeeFilter: Boolean,
    onEvent: (LoadsEvent) -> Unit,
) {
    val filter = state.filter

    Column(verticalArrangement = Arrangement.spacedBy(ChipRowGap)) {
        FlowRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 0.dp),
            horizontalArrangement = Arrangement.spacedBy(ChipGap),
            verticalArrangement = Arrangement.spacedBy(ChipRowGap)
        ) {
            SectionLabel("Filter")
            CustomerFilterChip(
                customers = state.customers,
                selectedCustomerId = filter.customerId,
                onSelect = { onEvent(LoadsEvent.OnFilterByCustomer(it)) }
            )
            if (showEmployeeFilter) {
                EmployeeFilterChip(
                    users = state.users,
                    selectedEmployeeId = filter.employeeId,
                    onSelect = { onEvent(LoadsEvent.OnFilterByEmployee(it)) }
                )
            }
        }
    }
}

internal val ChipGap = 6.dp
internal val ChipRowGap = (-10).dp

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun FlowRowScope.SectionLabel(text: String) {
    Text(
        text = text,
        fontSize = 14.sp,
        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
        modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
    )
}

/** The customer filter label: the contact name when present, otherwise the company name. */
private fun customerFilterLabel(customer: Customer): String =
    customer.contactName.ifBlank { customer.companyName }

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CustomerFilterChip(
    customers: List<Customer>,
    selectedCustomerId: String?,
    onSelect: (String?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val selectedLabel = customers.firstOrNull { it.id == selectedCustomerId }
        ?.let { customerFilterLabel(it) }
    ExposedDropdownMenuBox(expanded = expanded, onExpandedChange = { expanded = it }) {
        FilterChip(
            selected = selectedCustomerId != null,
            onClick = { expanded = true },
            label = { Text(if (selectedLabel != null) "Customer: $selectedLabel" else "All Customers") },
            modifier = Modifier.exposedDropdownSize(false),
            border = FilterChipDefaults.filterChipBorder(
                enabled = true,
                borderColor = MaterialTheme.colorScheme.tertiary,
                selected = selectedCustomerId != null
            ),
            colors = FilterChipDefaults.filterChipColors(
                containerColor = MaterialTheme.colorScheme.background,
                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                selectedLabelColor = MaterialTheme.colorScheme.onTertiary
            )
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MaterialTheme.colorScheme.background,
            modifier = Modifier.widthIn(min = 190.dp)
        ) {
            if (selectedCustomerId != null) {
                DropdownMenuItem(
                    text = { Text("All Customers") },
                    onClick = { onSelect(null); expanded = false }
                )
            }
            customers.forEach { customer ->
                DropdownMenuItem(
                    text = { Text(customerFilterLabel(customer)) },
                    onClick = { onSelect(customer.id); expanded = false }
                )
            }
        }
    }
}

/**
 * A pickup/drop location filter chip; [label] is the singular stop name ("Pickup"/"Drop"). Opens
 * the searchable location picker rather than a dropdown — the location list is large and a
 * dropdown composes every item eagerly.
 */
@Composable
internal fun LocationFilterChip(
    label: String,
    locations: List<Location>,
    selectedLocationId: String?,
    onSelect: (String?) -> Unit
) {
    var showPicker by remember { mutableStateOf(false) }
    val selectedLabel = locations.firstOrNull { it.id == selectedLocationId }?.name
    FilterChip(
        selected = selectedLocationId != null,
        onClick = { showPicker = true },
        label = { Text(if (selectedLabel != null) "$label: $selectedLabel" else "All ${label}s") },
        border = FilterChipDefaults.filterChipBorder(
            enabled = true,
            borderColor = MaterialTheme.colorScheme.tertiary,
            selected = selectedLocationId != null
        ),
        colors = FilterChipDefaults.filterChipColors(
            containerColor = MaterialTheme.colorScheme.background,
            selectedContainerColor = MaterialTheme.colorScheme.tertiary,
            selectedLabelColor = MaterialTheme.colorScheme.onTertiary
        )
    )
    if (showPicker) {
        LocationSearchDialog(
            locations = locations,
            nameFieldLabel = "Location name",
            initialName = "",
            initialCity = "",
            initialState = "",
            onSelect = { onSelect(it.id); showPicker = false },
            onNewLocation = { _, _, _ -> },
            onDismiss = { showPicker = false },
            allowNewLocation = false,
            clearLabel = "All ${label}s",
            onClear = { onSelect(null); showPicker = false },
        )
    }
}

/**
 * A customer/carrier filter chip; [label] is the singular name ("Customer"/"Carrier"). Opens the
 * searchable customer picker in select-only mode. Carriers share the customer's contact/company/phone
 * shape, so callers map them to [Customer] to reuse the same picker.
 */
@Composable
internal fun ContactFilterChip(
    label: String,
    contacts: List<Customer>,
    selectedId: String?,
    onSelect: (String?) -> Unit
) {
    var showPicker by remember { mutableStateOf(false) }
    val selectedLabel = contacts.firstOrNull { it.id == selectedId }?.let { customerFilterLabel(it) }
    FilterChip(
        selected = selectedId != null,
        onClick = { showPicker = true },
        label = { Text(if (selectedLabel != null) "$label: $selectedLabel" else "All ${label}s") },
        border = FilterChipDefaults.filterChipBorder(
            enabled = true,
            borderColor = MaterialTheme.colorScheme.tertiary,
            selected = selectedId != null
        ),
        colors = FilterChipDefaults.filterChipColors(
            containerColor = MaterialTheme.colorScheme.background,
            selectedContainerColor = MaterialTheme.colorScheme.tertiary,
            selectedLabelColor = MaterialTheme.colorScheme.onTertiary
        )
    )
    if (showPicker) {
        CustomerSearchDialog(
            customers = contacts,
            initialNewCustomer = null,
            onSelect = { onSelect(it.id); showPicker = false },
            onNewCustomer = {},
            onDismiss = { showPicker = false },
            title = "Select $label",
            allowNewCustomer = false,
            clearLabel = "All ${label}s",
            onClear = { onSelect(null); showPicker = false },
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun EmployeeFilterChip(
    users: List<DatabaseUserModel>,
    selectedEmployeeId: String?,
    onSelect: (String?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val selectedLabel = users.firstOrNull { it.id == selectedEmployeeId }?.displayName
    ExposedDropdownMenuBox(expanded = expanded, onExpandedChange = { expanded = it }) {
        FilterChip(
            selected = selectedEmployeeId != null,
            onClick = { expanded = true },
            label = { Text(if (selectedLabel != null) "Driver: $selectedLabel" else "All Drivers") },
            modifier = Modifier.exposedDropdownSize(false),
            border = FilterChipDefaults.filterChipBorder(
                enabled = true,
                borderColor = MaterialTheme.colorScheme.tertiary,
                selected = selectedEmployeeId != null
            ),
            colors = FilterChipDefaults.filterChipColors(
                containerColor = MaterialTheme.colorScheme.background,
                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                selectedLabelColor = MaterialTheme.colorScheme.onTertiary
            )
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MaterialTheme.colorScheme.background,
            modifier = Modifier.widthIn(min = 190.dp)
        ) {
            if (selectedEmployeeId != null) {
                DropdownMenuItem(
                    text = { Text("All Drivers") },
                    onClick = { onSelect(null); expanded = false }
                )
            }
            users.forEach { user ->
                DropdownMenuItem(
                    text = { Text(user.displayName) },
                    onClick = { onSelect(user.id); expanded = false }
                )
            }
        }
    }
}
